import { PIXEL_DATE_METRICS, ReportType } from '../reporting/reports';
import type {
  DimensionMetadataDto,
  FormulaTokenDto,
  MetricMetadataDto,
} from '../../../api/generated/models';
import type { ReportingConfigEvent } from './campaignForm.types';

export const MAX_FORMULA_TOKENS = 64;

export const metricKeyReportFamily = (key: string): ReportType =>
  key === 'advertiser_event_count' ? 'conversion' : 'impression';

export const operandReportFamily = (
  option: { kind: string; key: string }
): ReportType | null => {

  if (option.kind === 'event' || option.kind === 'matched_event') return 'impression';

  if (option.kind === 'sum_event' || option.kind === 'count_event') return 'conversion';
  if (option.key === 'advertiser_event_count') return 'conversion';
  if (PIXEL_DATE_METRICS.has(option.key)) return 'impression';
  return null; // calc-metric chaining ref — family not known here
};

/**
 * Chip families for one operand. The metric catalogue is authoritative where it knows the key —
 * it can legitimately list both — and event operands fall back to their kind, which is what
 * decides their family.
 */
/** The family a saved calc metric reports in — its formula decides, else its override target. */
export const calcMetricFamily = (calc: {
  formula?: { tokens?: FormulaTokenDto[] } | null;
  target_mode?: string | null;
  target_column?: string | null;
}): ReportType | null =>
  calcRowReportFamily(calc.formula?.tokens) ??
  (calc.target_mode === 'override' && calc.target_column
    ? metricKeyReportFamily(calc.target_column)
    : null);

export const operandReportTypes = (
  option: FormulaOperandOption,
  catalogTypes: ReportType[] | undefined
): ReportType[] => {
  if (catalogTypes && catalogTypes.length > 0) return catalogTypes;
  if (option.family) return [option.family];
  const family = operandReportFamily(option);
  return family ? [family] : [];
};

export const calcRowReportFamily = (tokens: FormulaTokenDto[] | undefined): ReportType | null => {
  const refs = (tokens || []).filter((t) => t.t === 'ref');
  if (refs.length === 0) return null;

  if (
    refs.some(
      (t) =>
        (t.kind === 'metric' && t.key === 'advertiser_event_count') ||
        t.kind === 'sum_event' ||
        t.kind === 'count_event'
    )
  ) {
    return 'conversion';
  }
  return 'impression';
};

export const buildCatalogKeySet = (
  metrics: MetricMetadataDto[],
  dimensions: DimensionMetadataDto[]
): Set<string> =>
  new Set<string>([
    ...metrics.map((m) => m.key),
    ...dimensions.map((d) => d.key),

    'event',
  ]);

export const SLUG_PATTERN = /^[a-z0-9_]+$/;

export interface FormulaOperandOption {

  kind: 'metric' | 'event' | 'sum_event' | 'matched_event' | 'count_event';
  key: string;
  label: string;

  /** Set for calculated-metric refs, whose family comes from their own formula, not their kind. */
  family?: ReportType | null;
}

export interface LabelOption {
  key: string;
  value: string;
}

export const buildCustomLabelOptions = (
  metrics: MetricMetadataDto[],
  dimensions: DimensionMetadataDto[]
): LabelOption[] => [
  ...metrics.map((metric) => ({ key: metric.key, value: `${metric.label} (Metric)` })),
  ...dimensions.map((dimension) => ({
    key: dimension.key,
    value: `${dimension.label} (Dimension)`,
  })),
];

export const buildOverrideTargetOptions = (metrics: MetricMetadataDto[]): LabelOption[] =>
  metrics.map((metric) => ({ key: metric.key, value: `${metric.label} (Metric)` }));

export const buildOverrideTargetKeySet = (options: LabelOption[]): Set<string> =>
  new Set(options.map((option) => option.key));

export const buildFormulaOperandOptions = (
  metrics: MetricMetadataDto[],
  reportingEvents: ReportingConfigEvent[],

  calcMetrics: Array<{ key: string; label: string; family?: ReportType | null }> = []
): FormulaOperandOption[] => {
  const metricOptions: FormulaOperandOption[] = metrics.map((metric) => ({
    kind: 'metric',
    key: metric.key,
    label: `${metric.label} (Metric)`,
  }));

  const activeEvents = reportingEvents.filter(
    (event) => event.is_active !== false && String(event.event || '').trim().length > 0
  );
  const slug = (event: ReportingConfigEvent) =>
    String(event.event || '')
      .trim()
      .toLowerCase();
  const eventLabel = (event: ReportingConfigEvent) => event.display_label || event.event;

  const isAmountEvent = (s: string) => s.endsWith('sum') || s.endsWith('commission');

  // Named to match the reporting column this operand compiles to, so it is findable.
  const eventOptions: FormulaOperandOption[] = activeEvents.map((event) => ({
    kind: 'event',
    key: slug(event),
    label: `${eventLabel(event)} (Attributed)`,
  }));

  const sumEventOptions: FormulaOperandOption[] = activeEvents
    .filter((event) => isAmountEvent(slug(event)))
    .map((event) => ({
      kind: 'sum_event',
      key: slug(event),
      label: `${eventLabel(event)} (Total $)`,
    }));

  const countEventOptions: FormulaOperandOption[] = activeEvents
    .filter((event) => !isAmountEvent(slug(event)))
    .map((event) => ({
      kind: 'count_event',
      key: slug(event),
      label: `${eventLabel(event)} (Count)`,
    }));

  const matchedEventOptions: FormulaOperandOption[] = activeEvents
    .filter((event) => !isAmountEvent(slug(event)))
    .map((event) => ({
      kind: 'matched_event',
      key: slug(event),
      label: `Matched Impression (${eventLabel(event)})`,
    }));

  const calcOptions: FormulaOperandOption[] = calcMetrics
    .filter((c) => String(c.key || '').trim().length > 0)
    .map((c) => ({
      kind: 'metric',
      key: String(c.key).trim(),
      label: `${c.label || c.key} (Calculated)`,
      family: c.family ?? null,
    }));

  return [
    ...metricOptions,
    ...eventOptions,
    ...countEventOptions,
    ...sumEventOptions,
    ...matchedEventOptions,
    ...calcOptions,
  ];
};

// Slugifies the WHOLE label so distinct labels get distinct keys, then appends a number
// on collision.
export const deriveKeyFromLabel = (label: string, existingKeys: Set<string>): string => {
  let base = String(label || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!base) return '';
  base = base.slice(0, 60);
  if (!existingKeys.has(base)) return base;
  let suffix = 2;
  while (existingKeys.has(`${base}${suffix}`)) suffix += 1;
  return `${base}${suffix}`;
};

export const deriveOverrideCalcKey = (targetColumn: string): string => {
  const base = String(targetColumn || '')
    .trim()
    .toLowerCase();
  if (!base) return '';
  const candidate = `calc_${base}`.replace(/[^a-z0-9_]/g, '_');
  return candidate.slice(0, 64);
};

export const clampDecimals = (value: number): number => {
  if (!Number.isFinite(value)) return 2;
  return Math.min(6, Math.max(0, Math.round(value)));
};

export const normalizeTokensForApi = (tokens: FormulaTokenDto[]): FormulaTokenDto[] =>
  tokens.map((tok) => {
    if (tok.t === 'num') {
      return { t: 'num', v: Number(tok.v) };
    }
    if (tok.t === 'ref' && (tok.kind === 'event' || tok.kind === 'count_event')) {
      return {
        t: 'ref',
        kind: tok.kind,
        key: String(tok.key || '')
          .trim()
          .toLowerCase(),
      };
    }
    if (tok.t === 'ref') {
      return {
        t: 'ref',
        kind: tok.kind,
        key: String(tok.key || '').trim(),
      };
    }
    if (tok.t === 'op') {
      return { t: 'op', v: tok.v };
    }
    return { t: tok.t };
  });

export const operandOptionKey = (option: FormulaOperandOption): string =>
  `${option.kind}:${option.key}`;
